import React, { useEffect, useState } from "react";
import { Dropdown } from "react-bootstrap";

import Card from "../../../../components/card";
import HttpManager from "../../../../services/HttpManager";
import useEsTelefono from "../../../../utils/esTelefono";
import { EventDetailDTO, GraduadasDTO } from "../../../../models/EventDTO";
import S from "./strings";
import { Chip, Modal, blobErrorText, dinero, errorText, fechaEvento, guardarBlob, slug } from "./comun";
import EventForm from "./eventForm";
import GraduadasTab from "./graduadasTab";
import EntradaTab from "./entradaTab";
import ResultadoTab from "./resultadoTab";
import AgregarGraduadas from "./agregarGraduadas";
import AgregarOtro from "./agregarOtro";
import OtrosInvitados from "./otrosInvitados";
import QueVaAPasar from "./queVaAPasar";

type Tab = "graduadas" | "entrada" | "resultado";
interface Props { campusId: number; id: number; onBack: () => void; notify: (t: string, ok?: boolean) => void; }

/*
	Detalle del evento (Módulos Claros, 2026-11-01): tres pestañas (Graduadas · Entrada · Resultado). Arriba solo lo que se usa:
	Agregar Graduadas (lo que era Elegibles), Imprimir Invitaciones (el PDF de todas) y «Más» para lo raro (Agregar Otro, Otros
	Invitados, Repartir Mesas, Anular Evento). El estado ya no se elige: Próximo / Pasado salen de la fecha y Anular es su propio botón.
*/
const EventDetail: React.FC<Props> = ({ campusId, id, onBack, notify }) => {
	const http = HttpManager.getInstance();
	const movil = useEsTelefono();
	const [ev, setEv] = useState<EventDetailDTO | null>(null);
	const [grad, setGrad] = useState<GraduadasDTO | null>(null);
	const [tab, setTab] = useState<Tab>("graduadas");
	const [modal, setModal] = useState<"" | "editar" | "agregar" | "otro" | "otros" | "mesas" | "anular">("");
	const [busy, setBusy] = useState<boolean>(false);
	const [conservar, setConservar] = useState<boolean>(false);

	const cargar = () => {
		http.eventsService.details(campusId, id).then(r => setEv(r.data)).catch(e => notify(errorText(e), false));
		http.eventsService.graduadas(campusId, id).then(r => setGrad(r.data)).catch(e => notify(errorText(e), false));
	};
	useEffect(() => { cargar();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [campusId, id]);

	if (!ev) return <div className="loader" />;
	const anulado = ev.status === "anulado";
	const soloLectura = ev.isHistoric || anulado;
	const vigentes = ev.attendees.filter(a => a.status === 1);
	const otros = grad?.others || [];
	const pdfTodas = () => {
		setBusy(true);
		http.eventsService.invitationsPdf(campusId, ev.id)
			.then(r => guardarBlob(r.data, `Invitaciones_${slug(ev.name)}.pdf`))
			.catch(async e => notify(await blobErrorText(e), false))
			.finally(() => setBusy(false));
	};
	const repartir = () => {
		setBusy(true);
		http.eventsService.assignTables(campusId, ev.id, conservar)
			.then(() => { setModal(""); notify(S.mesasOk); cargar(); })
			.catch(e => notify(errorText(e), false))
			.finally(() => setBusy(false));
	};
	const anular = () => {
		setBusy(true);
		http.eventsService.update(campusId, { id: ev.id, status: "anulado" })
			.then(() => { setModal(""); notify(S.anuladoOk); cargar(); })
			.catch(e => notify(errorText(e), false))
			.finally(() => setBusy(false));
	};
	const paquetes = (ev.guestTiers || []).map(t => S.paqueteCorto(t.guests, dinero(ev.currency, t.total), t.freeChild)).join(" · ");

	return (
		<div>
			<div className="wevCab">
				<div className="tit">
					<button type="button" className="wevVolver" onClick={onBack}>{S.eventos}</button>
					<h4>{ev.name}{anulado ? <Chip tono="oro">{S.anulado}</Chip> : null}</h4>
					<div className="wevInfo">
						<span><b>{fechaEvento(ev.dateTime || ev.date)}</b></span>
						{ev.venue ? <span>{ev.venue}</span> : null}
						<span>{S.paquete} <b>{dinero(ev.currency, ev.packagePrice)}</b></span>
						{paquetes ? <span>{S.paquetes} <b>{paquetes}</b></span> : null}
						<span>{S.invitadoExtra} <b>{dinero(ev.currency, ev.extraGuestPrice)}</b></span>
						<span>{S.nino} <b>{dinero(ev.currency, ev.childPrice)}</b></span>
						{ev.capacity ? <span>{S.cupo} <b>{S.deCupo(ev.counts.total, ev.capacity)}</b></span> : null}
					</div>
				</div>
				<div className="acc">
					{soloLectura ? null : <button type="button" className="btn btn-primary btn-sm wevBoton" onClick={() => setModal("agregar")}>{S.agregarGraduadas}</button>}
					<button type="button" className="btn btn-outline-secondary btn-sm wevBoton" disabled={busy || vigentes.length === 0} onClick={pdfTodas}>{S.imprimirInvitaciones}</button>
					{soloLectura ? null : <button type="button" className="btn btn-outline-secondary btn-sm wevBoton" onClick={() => setModal("editar")}>{S.editar}</button>}
					{soloLectura && otros.length === 0 ? null : (
						<Dropdown className="wevMas" align="end">
							<Dropdown.Toggle as="button" type="button" className="btn btn-outline-secondary btn-sm wevBoton">{S.mas}</Dropdown.Toggle>
							<Dropdown.Menu popperConfig={{ strategy: "fixed" }} renderOnMount>
								{soloLectura ? null : <Dropdown.Item as="button" type="button" onClick={() => setModal("otro")}>{S.agregarOtro}</Dropdown.Item>}
								{otros.length ? <Dropdown.Item as="button" type="button" onClick={() => setModal("otros")}>{S.otrosInvitados(otros.length)}</Dropdown.Item> : null}
								{!soloLectura && ev.tableSize && vigentes.length ? <Dropdown.Item as="button" type="button" onClick={() => setModal("mesas")}>{S.repartirMesas}</Dropdown.Item> : null}
								{soloLectura ? null : <><Dropdown.Divider /><Dropdown.Item as="button" type="button" className="oro" onClick={() => setModal("anular")}>{S.anularEvento}</Dropdown.Item></>}
							</Dropdown.Menu>
						</Dropdown>
					)}
				</div>
			</div>
			<Card>
				<div style={{ padding: movil ? "12px 12px 4px 12px" : "12px 18px 4px 18px" }}>
					{tab === "graduadas" ? (
						<GraduadasTab campusId={campusId} evento={ev} grad={grad} soloLectura={soloLectura} reload={cargar} notify={notify}
							tabs={<Pestanas tab={tab} setTab={setTab} n={grad?.rows.length ?? null} />} />
					) : <>
						<Pestanas tab={tab} setTab={setTab} n={grad?.rows.length ?? null} />
						<div style={{ marginTop: 12 }}>
							{tab === "entrada" ? <EntradaTab campusId={campusId} evento={ev} soloLectura={soloLectura} reload={cargar} notify={notify} /> : null}
							{tab === "resultado" ? <ResultadoTab campusId={campusId} evento={ev} soloLectura={soloLectura} reload={cargar} notify={notify} /> : null}
						</div>
					</>}
				</div>
			</Card>
			{modal === "editar" ? <EventForm campusId={campusId} evento={ev} onClose={() => setModal("")} onSaved={(_x, texto) => { setModal(""); notify(texto); cargar(); }} /> : null}
			{modal === "agregar" ? <AgregarGraduadas campusId={campusId} evento={ev} onClose={() => setModal("")} onDone={(t, ok) => { notify(t, ok); cargar(); }} /> : null}
			{modal === "otro" ? <AgregarOtro campusId={campusId} evento={ev} onClose={() => setModal("")} onDone={(t) => { setModal(""); notify(t); cargar(); }} /> : null}
			{modal === "otros" ? <OtrosInvitados campusId={campusId} evento={ev} ids={otros} onClose={() => setModal("")} reload={cargar} notify={notify} /> : null}
			{modal === "mesas" ? (
				<Modal title={S.repartirMesas} onClose={() => setModal("")} ancho={420}>
					<div className="wev-datos" style={{ marginBottom: 12 }}>
						<div><span className="wau-lab">{S.porMesa}</span><span className="wau-num">{ev.tableSize}</span></div>
						<div><span className="wau-lab">{S.asistentes}</span><span className="wau-num">{vigentes.length}</span></div>
					</div>
					<label className="wev-check" style={{ marginBottom: 12 }}><input type="checkbox" checked={conservar} onChange={e => setConservar(e.target.checked)} /><span>{S.conservarManuales}</span></label>
					<div className="wau-acciones">
						<button type="button" className="wau-btn s" onClick={() => setModal("")} disabled={busy}>{S.volver}</button>
						<button type="button" className="wau-btn p" onClick={repartir} disabled={busy}>{S.repartir}</button>
					</div>
				</Modal>
			) : null}
			{modal === "anular" ? (
				<Modal title={S.anularEvento} onClose={() => !busy && setModal("")} ancho={520}>
					<QueVaAPasar campusId={campusId} pedir={() => http.eventsService.previewUpdate(campusId, { eventId: ev.id, action: "anular" })}
						confirmar={S.anularEvento} busy={busy} onVolver={() => setModal("")} onConfirmar={anular} />
				</Modal>
			) : null}
		</div>
	);
};

const Pestanas: React.FC<{ tab: Tab; setTab: (t: Tab) => void; n: number | null }> = ({ tab, setTab, n }) => (
	<ul className="nav nav-pills waTabs" style={{ gap: 4 }}>
		{(["graduadas", "entrada", "resultado"] as Tab[]).map(t => (
			<li className="nav-item" key={t}>
				<button type="button" className={"nav-link btn btn-sm " + (tab === t ? "active" : "")} style={{ padding: "4px 12px", fontSize: 15 }} onClick={() => setTab(t)}>
					{S.tabs[t]}{t === "graduadas" && n ? <span className="waTabCuenta">{n}</span> : null}
				</button>
			</li>
		))}
	</ul>
);

export default EventDetail;
